import * as THREE from 'three';
import { noteFootprint } from '../../render/contact-shadow';
import { normalFrom } from '../../render/tex-normal';
import { envMap } from '../../render/env';
import { onQuality } from '../../platform/quality';
/* ============================================================
   廢墟工具箱 —— 貼圖、材質、合批
   ============================================================

   畫風取自使用者的原型 `world_sample.html` §4（「廢棄都市」）。
   那一版的地圖版面已經作廢（2026-08-04 使用者拍板改成空曠場地），
   但**畫風留下來** —— 使用者原話：「根據剛才的廢棄都市畫風製作」。

   §4 開頭那段規則照抄，因為它是整套配色的理由：

     · 威脅色（紅橙紫藍）與玩家綠**不可出現在地形上**
     · 地形量體的平均明度必須**低於敵人裝甲 96**
     · 色相只要壓低飽和就不會跟威脅色搶讀

   ## 兩個踩過的坑（都是靜默的）

   1. **`CanvasTexture` 一定要設 `encoding = sRGBEncoding`。**
      three r128 的預設是 LinearEncoding：`#0d1016`（sRGB 5%）會被當成
      linear 5% 直接輸出，變成 sRGB 的 24% —— 一張近黑的貼圖亮成中灰，
      而且**看起來只是「有點亮」**，不會有任何錯誤。
   2. **headless 沒有 document**：Node 測試會走到這裡，
      所以每個貼圖函式都先問 `typeof document`，拿不到就回 null
      （材質拿到 `map:null`，three 收得下）。
   ============================================================ */

/** headless 沒有 document；回 null 讓上游早退。 */
function cv(w, h): any {
  if (typeof document === 'undefined') return null;
  var c: any = document.createElement('canvas');  // det-ok: 上一行就是 headless 的守門員
  c.width = w; c.height = h; return c;
}
function mkt(c, rep?) {
  var t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.encoding = (THREE as any).sRGBEncoding;      // 見檔頭第 1 點
  t.anisotropy = 8;
  if (rep) t.repeat.set(rep, rep);
  return t;
}
function speck(g, n, W, H, r, gg, b, a) {
  for (var i = 0; i < n; i++) {
    var s = Math.random() * 3.2 + .5;             // det-ok: 程序貼圖，純視覺不進模擬
    g.fillStyle = 'rgba(' + (r + Math.random() * 54 | 0) + ',' + (gg + Math.random() * 54 | 0) + ',' + (b + Math.random() * 54 | 0) + ',' + (Math.random() * a) + ')';  // det-ok: 同上
    g.fillRect(Math.random() * W, Math.random() * H, s, s);  // det-ok: 同上
  }
}
function crack(g, n, W, H, col, lw) {
  for (var i = 0; i < n; i++) {
    g.strokeStyle = col; g.lineWidth = Math.random() * lw + .3; g.beginPath();  // det-ok: 同上
    var x = Math.random() * W, y = Math.random() * H; g.moveTo(x, y);  // det-ok: 同上
    for (var s = 0; s < 7; s++) { x += (Math.random() - .5) * W * .22; y += (Math.random() - .5) * H * .22; g.lineTo(x, y); }  // det-ok: 同上
    g.stroke();
  }
}
function streak(g, n, W, H, col) {
  for (var i = 0; i < n; i++) {
    var x = Math.random() * W, y = Math.random() * H, h = H * (.08 + Math.random() * .4);  // det-ok: 同上
    var gr = g.createLinearGradient(x, y, x, y + h);
    gr.addColorStop(0, col); gr.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = gr; g.fillRect(x, y, 1 + Math.random() * 4, h);  // det-ok: 同上
  }
}

/* 外牆：窗格是「這是建築不是方塊」的唯一線索。

   ⚠️ **窗要同時畫兩張**：albedo（白天看得到的窗框與空洞）與
   **emissive（只有還亮著的那幾格）**。只有 albedo 的話，在黃昏這種
   低照度的場景裡窗戶跟牆一樣暗 —— 2026-08-04 之前的版本就是這樣，
   建築在畫面上讀成一塊沒有內容的黑色量體。

   emissive 是「這座城還沒完全死」的唯一訊號，也是暗畫面裡的亮部錨點。
   兩張圖必須在**同一個迴圈**裡畫，否則窗格位置對不上（各自 random）。

   色相分工（避免跟戰鬥語彙搶讀）：
     · 鈉燈暖黃 `#ffcf96` —— 多數。刻意比威脅色 amber `#ff8a3d` 淡且不飽和
     · 冷白日光燈 `#bcd6ff` —— 少數。混合色溫才讀得出「這是很多戶人家」
     · 兩者都只佔窗格的 6%，而且不閃 —— 會動的亮點是敵人的地盤 */
function texFacade(base, dense) {
  /* headless 回**一對 null** 而不是單一 null —— 呼叫端一律解構
     `.map`／`.em`，回 null 的話 Node 測試會炸在讀屬性上。 */
  var W = 512, c = cv(W, W); if (!c) return { map: null, em: null };
  var ec = cv(W, W);
  var g = c.getContext('2d'), e = ec.getContext('2d');
  g.fillStyle = base; g.fillRect(0, 0, W, W);
  e.fillStyle = '#000000'; e.fillRect(0, 0, W, W);
  speck(g, 16000, W, W, 26, 30, 36, .42);
  var cols = dense ? 12 : 8, rows = dense ? 16 : 11, pw = W / cols, ph = W / rows;
  for (var r = 0; r < rows; r++) for (var k = 0; k < cols; k++) {
    var x = k * pw + pw * .17, y = r * ph + ph * .16, w = pw * .66, h = ph * .56, q = Math.random();  // det-ok: 同上
    var lit = q < .038 ? (q < .030 ? '#ffcf96' : '#bcd6ff') : null;
    if (lit) g.fillStyle = lit;
    else if (q < .13) g.fillStyle = '#4e5a68';    // 反光
    else g.fillStyle = '#0d1116';                 // 空洞
    g.fillRect(x, y, w, h);
    if (lit) {
      /* 亮窗：emissive 那張畫同一格，並在 albedo 上壓一道窗框的橫檔 ——
         一格純色的亮方塊讀成貼紙，有橫檔才讀成窗。 */
      e.fillStyle = lit; e.fillRect(x, y, w, h);
      e.fillStyle = 'rgba(0,0,0,.55)'; e.fillRect(x, y + h * .42, w, h * .16);
      g.fillStyle = 'rgba(10,12,16,.5)'; g.fillRect(x, y + h * .42, w, h * .16);
    } else if (Math.random() < .42) {             // det-ok: 同上
      g.fillStyle = '#080a0e';
      g.fillRect(x + Math.random() * w * .5, y + Math.random() * h * .4, w * .58, h * .68);  // det-ok: 同上
    }
    g.strokeStyle = 'rgba(12,15,20,.72)'; g.lineWidth = 1.4; g.strokeRect(x, y, w, h);
    if (!lit && Math.random() < .16) {            // det-ok: 崩掉一格
      g.fillStyle = base; g.fillRect(x - pw * .14, y - ph * .12, pw * .9, ph * .86);
    }
  }
  for (var b = 0; b < rows; b++) {                // 樓板橫帶
    g.fillStyle = 'rgba(255,255,255,.05)'; g.fillRect(0, b * ph - 1.5, W, 2.5);
    g.fillStyle = 'rgba(0,0,0,.30)'; g.fillRect(0, b * ph + 1, W, 2.5);
  }
  streak(g, 44, W, W, 'rgba(84,54,30,.40)');      // 鏽痕：低飽和的暖，不是橙色訊號
  crack(g, 26, W, W, 'rgba(8,10,14,.42)', 2);
  return { map: mkt(c), em: mkt(ec) };
}
/* 混凝土。**斑點與髒污一律偏冷**（`66,62,54` → `54,62,70`）——
   2026-08-04 改冷調時一起換：斑點的色偏必須跟底色同方向，
   反向的話兩者互相抵銷成沒有顏色的中灰（這正是更早那一版
   「偏暗又沒質感」的一半原因，只是當時抵銷的方向相反）。 */
function texConcrete(tint) {
  var W = 512, c = cv(W, W); if (!c) return null;
  var g = c.getContext('2d');
  g.fillStyle = tint; g.fillRect(0, 0, W, W);
  speck(g, 20000, W, W, 54, 62, 70, .52);
  crack(g, 84, W, W, 'rgba(14,17,20,.52)', 2.6);
  for (var i = 0; i < 9; i++) { g.fillStyle = 'rgba(255,255,255,.045)'; g.fillRect(0, i * W / 9, W, 3); }
  for (var j = 0; j < 58; j++) {
    var x = Math.random() * W, y = Math.random() * W, r = 14 + Math.random() * 76;  // det-ok: 同上
    var gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, 'rgba(16,20,24,.34)'); gr.addColorStop(1, 'rgba(24,20,16,0)');
    g.fillStyle = gr; g.beginPath(); g.arc(x, y, r, 0, 6.283); g.fill();
  }
  streak(g, 30, W, W, 'rgba(74,50,30,.30)');
  return mkt(c);
}
function texPanel() {
  var W = 256, c = cv(W, W); if (!c) return null;
  var g = c.getContext('2d');
  g.fillStyle = '#4a4e53'; g.fillRect(0, 0, W, W);
  speck(g, 6000, W, W, 60, 60, 62, .4);
  g.strokeStyle = 'rgba(16,20,26,.6)'; g.lineWidth = 3;
  for (var i = 0; i <= W; i += 64) {
    g.beginPath(); g.moveTo(i, 0); g.lineTo(i, W); g.stroke();
    g.beginPath(); g.moveTo(0, i); g.lineTo(W, i); g.stroke();
  }
  g.fillStyle = 'rgba(20,24,30,.75)';
  for (var k = 0; k < 120; k++) {
    g.beginPath();
    g.arc(10 + Math.floor(Math.random() * 4) * 64, 10 + Math.floor(Math.random() * 4) * 64, 3, 0, 6.283); g.fill();  // det-ok: 同上
  }
  streak(g, 34, W, W, 'rgba(86,56,34,.40)');
  return mkt(c);
}
function texRust() {
  var W = 256, c = cv(W, W); if (!c) return null;
  var g = c.getContext('2d');
  g.fillStyle = '#4e3a2c'; g.fillRect(0, 0, W, W);
  speck(g, 9000, W, W, 60, 42, 30, .55);
  for (var i = 0; i < 70; i++) {
    var x = Math.random() * W, y = Math.random() * W, r = 6 + Math.random() * 40;  // det-ok: 同上
    var gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, 'rgba(96,58,32,.4)'); gr.addColorStop(1, 'rgba(96,58,32,0)');
    g.fillStyle = gr; g.beginPath(); g.arc(x, y, r, 0, 6.283); g.fill();
  }
  crack(g, 26, W, W, 'rgba(8,12,18,.5)', 2);
  return mkt(c);
}
/* 危險條紋：灰白／炭黑。**不用橙黑** —— 那是威脅色 amber 的地盤。 */
function texHaz() {
  var W = 128, c = cv(W, W); if (!c) return null;
  var g = c.getContext('2d');
  g.fillStyle = '#191b1c'; g.fillRect(0, 0, W, W);
  g.strokeStyle = '#96a0a8'; g.lineWidth = 26;
  for (var i = -140; i < 260; i += 54) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i + W, W); g.stroke(); }
  speck(g, 3000, W, W, 26, 24, 20, .6);
  crack(g, 10, W, W, 'rgba(10,12,16,.5)', 2);
  return mkt(c);
}

var _tx: any = null;
/** 貼圖庫（一次建好、之後共用）。

    ⚠️ **底色一律是冷灰**（`#454b55` 這一族）。2026-08-04 之前是暖灰
    （`#4e4b45`，抄 `world_sample.html` 的黃昏廢墟），使用者指出那跟遊戲
    的機甲冷調不搭 —— 同樣的幾何、同樣的光，暖灰讀成日落的塵，
    冷灰才讀成月光下的水泥，而後者才是這款遊戲要的調性。

    ⚠️ 換色時**每一個值的明度都沒動**（暖／冷版的 luma 相同）：
    這一族的亮度是對著「敵人裝甲 96」訂的（見 `TM()` 開頭），
    順手調亮暗會讓建築跟敵人在畫面上打架。 */
export function TX() {
  if (_tx) return _tx;
  _tx = {
    fac: [texFacade('#454b55', false), texFacade('#4a5058', false), texFacade('#3f424a', true)],
    con: texConcrete('#4f5763'),
    conD: texConcrete('#373d45'),
    panel: texPanel(),
    rust: texRust(),
    haz: texHaz(),
  };
  return _tx;
}

function sm(o): any { o.flatShading = true; return new THREE.MeshStandardMaterial(o); }

var _tm: any = null;
/** 材質表。**色 × 貼圖平均值 ＝ 有效明度，一律低於敵人裝甲 96**。

    `facA/B/C` 掛 `emissiveMap`：只有還亮著的窗會發光（見 `texFacade`）。
    `emissiveIntensity` 0.85 是「暗場裡讀得到、但不會蓋過機體與威脅色」的值 ——
    調到 1.5 以上整棟樓會變成招牌，那是霓虹夜城不是廢墟。 */
export function TM() {
  if (_tm) return _tm;
  var T = TX();
  /* ⚠️ 這些 hex 是 **linear** 值（r128 沒有 ColorManagement，輸出時被
     sRGB 提亮將近兩倍）。2026-08-04 第一版照原型的 `0xa9a49a` 填，
     結果整片建築在近距離讀成**淺褐色紙箱**——原型那個值是配它自己的
     曝光與濃霧的，貼到我們這個近鏡頭上就過亮。一律再壓一階。 */
  /* `ei` 0.85 → 0.5：0.85 讓每一格亮窗都是純白，整棟樓讀成**電路板**。
     窗要「看得出有燈」而不是「在發光」——它的工作是暗畫面裡的細節，
     不是光源（真正的亮部錨點是火與探照燈）。 */
  var WEM: any = { emissive: 0xffffff, ei: .5 };
  _tm = {
    facA: sm({ map: T.fac[0].map, emissiveMap: T.fac[0].em, emissive: WEM.emissive, emissiveIntensity: WEM.ei, color: 0x7d8692, roughness: .93, metalness: .05 }),
    facB: sm({ map: T.fac[1].map, emissiveMap: T.fac[1].em, emissive: WEM.emissive, emissiveIntensity: WEM.ei, color: 0x7d8692, roughness: .93, metalness: .05 }),
    facC: sm({ map: T.fac[2].map, emissiveMap: T.fac[2].em, emissive: WEM.emissive, emissiveIntensity: WEM.ei, color: 0x727f8b, roughness: .94, metalness: .04 }),
    con: sm({ map: T.con, color: 0x808a93, roughness: .95, metalness: .03 }),
    conD: sm({ map: T.conD, color: 0x5f676f, roughness: .97, metalness: .02 }),
    trim: sm({ color: 0x151719, roughness: .86, metalness: .10 }),
    steel: sm({ map: T.panel, color: 0x878d94, roughness: .50, metalness: .58 }),
    /* 鏽蝕：飽和度要壓。原型的 `0x8d5231` 貼在 12 m 的貨櫃側面上是
       一整片橘紅，會直接跟威脅色 amber 搶讀（而且看起來像紙箱）。
       鏽是廢墟唯一的「顏色」，但它的位置是**細長構件**不是大面。 */
    rust: sm({ map: T.rust, color: 0x6f4e35, roughness: .93, metalness: .22 }),
    haz: sm({ map: T.haz, color: 0x88949d, roughness: .88, metalness: .10 }),
    mark: new THREE.MeshBasicMaterial({ color: 0x7c878e }),
    /* 頂端航障燈：暖白。`MeshBasicMaterial` 不吃光，所以它在任何時候都亮 */
    lit: new THREE.MeshBasicMaterial({ color: 0xffd8a8 }),
    far: sm({ color: 0x282c32, roughness: 1, metalness: .02 }),
  };
  /* 法線圖歸畫質設定管（見下）。註冊當下就會套用一次。 */
  _tx4n = T;
  onQuality(applyBuildingQuality);
  return _tm;
}

/* ---------- 法線圖：質感這一輪最大的單一項目 ----------

   從貼圖亮度算出來的（`render/tex-normal.ts`），不增加 draw call、
   不多畫一張圖。沒有它的話近景看每一面都是均質的平面著色 ——
   使用者說的「質感」有一半是這件事：同一盞燈打在有凹凸的面上
   才會出現明暗的細節。

   `strength` 逐材質分開：混凝土要顆粒（1.1）、鏽蝕要坑（1.6）、
   鋼板只要接縫（0.8，太深會讀成壓花鐵板）。

   ⚠️ **算出來就留著**（`_norms`）。八張 Sobel 加起來約 150 萬像素，
   每次切畫質都重算的話切換會卡住一秒 —— 而且重算會拿到不同的貼圖。 */
var _norms: any = null, _tx4n: any = null;
var NS = new THREE.Vector2(0.85, 0.85);
function buildNorms() {
  if (_norms || !_tx4n) return _norms;
  var T = _tx4n;
  _norms = {
    facA: normalFrom(T.fac[0].map, 1.0), facB: normalFrom(T.fac[1].map, 1.0),
    facC: normalFrom(T.fac[2].map, 1.0), con: normalFrom(T.con, 1.1),
    conD: normalFrom(T.conD, 1.1), steel: normalFrom(T.panel, 0.8),
    rust: normalFrom(T.rust, 1.6), haz: normalFrom(T.haz, 0.9),
  };
  return _norms;
}
function applyBuildingQuality(q) {
  var M = _tm; if (!M) return;
  if (q.buildingNormals) buildNorms();
  var keys = ['facA', 'facB', 'facC', 'con', 'conD', 'steel', 'rust', 'haz'];
  for (var i = 0; i < keys.length; i++) {
    var m = M[keys[i]]; if (!m) continue;
    m.normalMap = (q.buildingNormals && _norms) ? _norms[keys[i]] : null;
    if (m.normalMap) m.normalScale = NS;
    m.needsUpdate = true;
  }
  /* 鋼材的環境反射跟地面的積水同一個旗標 —— 兩者都是「反射」這一項成本。 */
  if (M.steel) { M.steel.envMap = q.floorEnv ? envMap() : null; M.steel.envMapIntensity = 0.5; M.steel.needsUpdate = true; }
}
/** 合批的順序（也是 `BUCK` 的鍵）。 */
export var MATKEYS = ['facA', 'facB', 'facC', 'con', 'conD', 'trim', 'steel', 'rust', 'haz', 'mark', 'lit', 'far'];

/* ---------- 幾何合批 ----------

   三千多個零件 → 十幾個 draw call。`put()` 丟零件、`fuse()` 收桶。
   UV 跟著世界尺寸縮放（`tile` ＝ 幾公尺一次貼圖），
   **不做的話一面 40 m 的牆會把 512² 的貼圖拉成一片糊**。 */

var _m4 = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
var _v = new THREE.Vector3(), _s = new THREE.Vector3();
export var BUCK: any = {};

export function resetBuckets() { BUCK = {}; }

function scaleUV(g, kind, w, h, d, tile) {
  var uv = g.attributes.uv; if (!uv) return;
  var i;
  if (kind === 'box') {
    var dim = [[d, h], [d, h], [w, d], [w, d], [w, h], [w, h]];
    for (i = 0; i < uv.count; i++) {
      var f = Math.floor(i / 6);
      uv.setXY(i, uv.getX(i) * dim[f][0] / tile, uv.getY(i) * dim[f][1] / tile);
    }
  } else if (kind === 'cyl') {
    var per = Math.PI * (w + d) / 2;
    for (i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * per / tile, uv.getY(i) * h / tile);
  } else {
    var k = Math.max(w, h, d) / tile;
    for (i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * k, uv.getY(i) * k);
  }
  uv.needsUpdate = true;
}

/** 丟一個零件進材質桶。`uvKind` ＝ `'box'`／`'cyl'`／其他；`tile` ＝ 幾公尺一次貼圖。 */
export function put(key, geo, uvKind, w, h, d, x, y, z, rx?, ry?, rz?, tile?) {
  var g = geo.clone();
  if (g.index) { var ni = g.toNonIndexed(); g.dispose(); g = ni; }
  if (tile) scaleUV(g, uvKind, w, h, d, tile);
  /* 接地影的足跡（見 `render/contact-shadow.ts`）。放在這裡而不是各個
     建造器裡，是因為**每一個零件都會經過 put()** —— 漏收的症狀是
     「某一種物件浮在地上」，而那種缺陷只有實看才發現得了。 */
  noteFootprint(y, w, h, d, x, z);
  _e.set(rx || 0, ry || 0, rz || 0); _q.setFromEuler(_e);
  _m4.compose(_v.set(x, y, z), _q, _s.set(w, h, d));
  g.applyMatrix4(_m4);
  (BUCK[key] = BUCK[key] || []).push(g);
}

function fuse(list) {
  var total = 0, i, hasUV = true;
  for (i = 0; i < list.length; i++) {
    total += list[i].attributes.position.count;
    if (!list[i].attributes.uv) hasUV = false;
  }
  var pos = new Float32Array(total * 3), nor = new Float32Array(total * 3);
  var uvs = hasUV ? new Float32Array(total * 2) : null, off = 0;
  for (i = 0; i < list.length; i++) {
    pos.set(list[i].attributes.position.array, off * 3);
    nor.set(list[i].attributes.normal.array, off * 3);
    if (hasUV) uvs!.set(list[i].attributes.uv.array, off * 2);
    off += list[i].attributes.position.count;
    list[i].dispose();
  }
  var g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  if (hasUV) g.setAttribute('uv', new THREE.BufferAttribute(uvs!, 2));
  g.computeBoundingSphere();
  return g;
}

/** 把所有材質桶合成 mesh 掛進 `group`，回傳做出幾顆。 */
export function flushBuckets(group: THREE.Group) {
  var M = TM(), made = 0;
  for (var i = 0; i < MATKEYS.length; i++) {
    var key = MATKEYS[i];
    if (!BUCK[key] || !BUCK[key].length) continue;
    var mesh = new THREE.Mesh(fuse(BUCK[key]), M[key]);
    mesh.frustumCulled = false;
    if (key !== 'mark' && key !== 'lit' && key !== 'far') { mesh.castShadow = true; mesh.receiveShadow = true; }
    group.add(mesh); made++;
  }
  resetBuckets();
  return made;
}
